import { Chat } from '@/config'
import { GroupDetailsModal, Layout, LayoutPlaceholderProps, Loading, View } from '@/components'
import React, { useCallback, useEffect } from 'react'
import { useChatListeners, useSyncUnreadState } from '@packages/chat/src/hooks'
import { Channel, ChannelProps, useChatContext, useChannelStateContext, useChannelActionContext } from 'stream-chat-react'
import { ChatChannelInstance, ChatChannel as ChatChannelType, ChatLocalMesage, isChatMarkableAsRead } from '@packages/chat'
import { Alert } from '@codeleap/modals'

export type ChatChannelProps = Partial<ChannelProps> & {
  children: React.ReactNode
  channelId: ChatChannelType['id']
  threadId?: ChatLocalMesage['id']
  listenersEnabled?: boolean
}

const removeMarkAsUnread = (channel: ChatChannelInstance) => {
  const isMarkableAsRead = isChatMarkableAsRead(channel)
  if (isMarkableAsRead) {
    Chat.Services.toggleReadChat(channel)
  }
}

export const ChatChannel = ({
  children,
  channelId,
  threadId,
  listenersEnabled = false,
  ...rest
}: ChatChannelProps) => {
  const channel = Chat.Store.state.use(state => state.channel)
  const thread = Chat.Store.state.use(state => state.thread)
  const { client } = useChatContext()
  const [error, setError] = React.useState<string>(null)
  const [loading, setIsLoading] = React.useState(false)

  const fetchChannel = useCallback(async (channelId: ChatChannelType['id']) => {
    setError(null)
    const storedChannel = Chat.Store.state.get().channel

    if (storedChannel?.id === channelId) {
      removeMarkAsUnread(storedChannel)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    Chat.Store.cancelSelectMode()

    const filter = { id: channelId }
    const sort = [{ last_message_at: - 1 }]

    const channels = await client.queryChannels(filter, sort, {
      watch: true,
      state: true,
    })

    const desiredChannel = channels?.filter(c => c.id === channelId)?.[0]

    if (!!desiredChannel) {
      Chat.Store.setChannel(desiredChannel)
      removeMarkAsUnread(desiredChannel)
      setIsLoading(false)
    } else {
      setError('Failed to load channel')
      setIsLoading(false)
    }
  }, [client])

  const fetchThread = useCallback(async (threadId: ChatLocalMesage['id']) => {
    try {
      const parentMessage = await client.getMessage(threadId)
      if (parentMessage?.message) {
        Chat.Store.setThread(parentMessage.message as unknown as ChatLocalMesage)
      } else {
        setError('Failed to load thread')
      }
    } catch (e) {
      setError('Failed to load thread')
    }
  }, [client])

  useEffect(() => {
    if (!!channelId && client) {
      fetchChannel(channelId)
    }

    return () => {
      Chat.Store.setChannel(null)
      Chat.Store.cancelSelectMode()
      Chat.Store.setIsMessageActionMenuOpen(false)
    }
  }, [channelId, client, fetchChannel])

  useEffect(() => {
    if (!!threadId && client) {
      fetchThread(threadId)
    }

    return () => {
      Chat.Store.setThread(null)
    }
  }, [threadId, client, fetchThread])

  const wrongChannel = !!channel && channelId !== channel?.id
  const wrongThread = threadId && threadId !== thread?.id && !!thread

  if (loading || !channel || (threadId && !thread) || wrongChannel || wrongThread) return <Loader error={error} loading={loading} />

  return (
    <Channel {...rest} channel={channel}>
      {children}
      {listenersEnabled && (
        <ChatChannelListeners />
      )}
    </Channel>
  )
}

const Loader = (props: LayoutPlaceholderProps) => (
  <div className='str-chat__channel'>
    <View style={['fullWidth', 'fullHeight', 'center', 'bg:neutralSolid0', 'column']}>
      <Layout.Placeholder {...props} />
    </View>
  </div>
)

const ChatChannelListeners = () => {
  const { setChannelUnreadUiState } = useChannelActionContext()
  const { channel, channelUnreadUiState } = useChannelStateContext()

  const navigateToChatList = useCallback(() => {
    GroupDetailsModal.close()
    Alert.modal.close()
  }, [])

  useChatListeners({ channel, navigateToChatList })

  useSyncUnreadState({
    channel,
    unreadState: channelUnreadUiState,
    setUnreadState: setChannelUnreadUiState,
  })

  return null
}
